import {
  Avatar,
  Banner,
  Flex,
  ProfileInfoCard,
  SlidingPanel,
  Text,
} from '@components';
import { Box, useTheme } from '@mui/material';
import { useState } from 'react';
import {
  StyledContainer,
  StyledNotificationToggle,
  StyledNotificationContainer,
} from './UserProfile.styles';
import { NotificationPreferences, SliderTypes } from './UserProfile.types';
import {
  getBankDetails,
  getPersonalInfo,
  getTaxDetails,
  SliderComponents,
} from './UserProfile.utils';
import WithUser, { WithUserProps } from '@HOCs/withUser';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import {
  useDeleteUserAvatarMutation,
  useUpdateUserMarketPreferencesMutation,
  useUserMarketPreferencesQuery,
} from '@features/api/userSlice';
import { UserMarketPreferences } from '@features/api/request/User';
import { useUploadAvatarMutation } from '@features/api/documentUploadSlice';

const UserProfile: React.FC<WithUserProps> = ({ user, refetchUser }) => {
  const theme = useTheme();
  const intl = useIntl();
  const [sliderType, setSliderType] = useState<SliderTypes>();
  const { data: marketingPreferences } = useUserMarketPreferencesQuery();
  const [uploadAvatarMutation] = useUploadAvatarMutation();
  const [deleteAvatarMutation] = useDeleteUserAvatarMutation();
  const [updateUserMarketPreferences] =
    useUpdateUserMarketPreferencesMutation();

  if (!user) return <></>;

  const onCloseSlider = (): void => {
    setSliderType(undefined);
  };

  const updateNotificationPreferences = async (
    type: keyof UserMarketPreferences,
    isChecked: boolean
  ) => {
    try {
      await updateUserMarketPreferences({
        [type]: isChecked,
      }).unwrap();
    } catch (e) {
      console.error(e);
    }
  };

  const handleAvatarIconClick = async (data: FormData) => {
    await uploadAvatarMutation(data).unwrap();
    refetchUser();
  };

  const handleAvatarDeleteIconClick = async () => {
    await deleteAvatarMutation().unwrap();
  };

  return (
    <StyledContainer>
      <Box>
        <Avatar
          user={{ ...user.basicDetails, avatar: user.avatar }}
          editable
          onIconClick={handleAvatarIconClick}
          onDeleteIconClick={handleAvatarDeleteIconClick}
        />
      </Box>
      {user.personalDetailsLocked ? (
        <Flex flex={1}>
          <Banner color='warning'>
            <Flex paddingY={6}>
              <Text
                variant='h6'
                fontWeight='fontWeightMedium'
                color={theme.palette.text.primary}
              >
                <FormattedMessage
                  id='profile.contactSupportEditProfile'
                  values={{
                    bold: (chunks: React.ReactNode[]): React.ReactNode => (
                      <Text
                        variant='h6'
                        fontWeight='fontWeightBold'
                        display='inline-flex'
                      >
                        {chunks}
                      </Text>
                    ),
                  }}
                />
              </Text>
            </Flex>
          </Banner>
        </Flex>
      ) : null}
      <ProfileInfoCard
        title={intl.formatMessage({
          id: 'investment.progress.personalDetails',
        })}
        data={getPersonalInfo(user)}
        onRightButtonPress={() => setSliderType(SliderTypes.PersonalDetails)}
        rightButtonTestId='personal-details-edit'
        hideRightButton={user.personalDetailsLocked}
      />
      <ProfileInfoCard
        title={intl.formatMessage({ id: 'investment.taxDetails.taxDetails' })}
        data={getTaxDetails(user)}
        onRightButtonPress={() => setSliderType(SliderTypes.TaxDetails)}
        rightButtonTestId='tax-details-edit'
        hideRightButton={user.personalDetailsLocked}
      />
      <ProfileInfoCard
        title={intl.formatMessage({ id: 'investment.bankDetails.bankDetails' })}
        data={getBankDetails(user)}
        onRightButtonPress={() => setSliderType(SliderTypes.BankDetails)}
        rightButtonTestId='bank-details-edit'
        hideRightButton={user.personalDetailsLocked}
      />
      <ProfileInfoCard
        title={intl.formatMessage({ id: 'misc.password' })}
        data={[]}
        rightButtonTitle={intl.formatMessage({
          id: 'misc.reset',
        })}
        onRightButtonPress={() => setSliderType(SliderTypes.Password)}
        rightButtonTestId='password-reset-button'
      >
        <Text
          variant='h6'
          fontWeight='fontWeightBold'
          color={theme.palette.grey.A100}
        >
          <FormattedMessage
            id='profile.passwordLastUpdated'
            values={{
              date: moment(user.passwordLastChangedAt).format('DD MMM YYYY'),
              time: moment(user.passwordLastChangedAt).format('HH:mm '),
            }}
          />
        </Text>
      </ProfileInfoCard>
      <ProfileInfoCard
        title={intl.formatMessage({ id: 'misc.notifications' })}
        data={[]}
        hideRightButton
      >
        <Flex flexDirection='column'>
          <StyledNotificationContainer hideBottomBorder>
            <StyledNotificationToggle
              label={
                intl.formatMessage({ id: 'profile.marketingEmails' }) + ':'
              }
              labelProps={{
                variant: 'h6',
                fontWeight: 'fontWeightMedium',
                sx: { color: theme.palette.grey.A100 },
              }}
              checked={marketingPreferences?.emailAlerts}
              onChange={(_, checked) =>
                updateNotificationPreferences(
                  NotificationPreferences.MarketingEmails,
                  checked
                )
              }
            />
          </StyledNotificationContainer>
        </Flex>
      </ProfileInfoCard>
      <SlidingPanel
        PaperProps={{ sx: { width: { xs: '100%', md: '40%' } } }}
        anchor='right'
        title={sliderType ?? ''}
        open={!!sliderType}
        onClose={onCloseSlider}
      >
        {SliderComponents(sliderType, onCloseSlider)}
      </SlidingPanel>
    </StyledContainer>
  );
};

export default WithUser(UserProfile);
